import React, { useCallback, useState } from 'react';
import { Check, Copy, Eye, Loader2, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';
import { useActiveSessionIdSafe } from '../../../contexts/SessionContext';
import { apiService } from '../../../services/ApiService';
import { JsonTreeNode } from '../../common/JsonTreeViewer';

interface VisionExtraction {
  at?: number;
  invocation_id?: string;
  raw?: Record<string, any>;
  cleaned?: Record<string, any>;
  parse_error?: string;
  raw_text?: string;
  [key: string]: any;
}

const itemCount = (payload?: Record<string, any>) =>
  Array.isArray(payload?.identified_items) ? payload!.identified_items.length : 0;

/**
 * Admin + debug-mode only: every vision pass in this session — the model's raw
 * output next to the cleaned payload the search used — so you can see exactly
 * what was read from each photo and what the cleanup dropped.
 */
const VisionDebugPanel: React.FC = () => {
  const { currentUser } = useAuth();
  const { debugMode } = useDebugMode();
  const sessionId = useActiveSessionIdSafe();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractions, setExtractions] = useState<VisionExtraction[]>([]);
  // What the search did with the photo (identity per part, request reading,
  // photo-observed axes, photo-designed questions).
  const [search, setSearch] = useState<Record<string, any>>({});
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get(`/api/chat/vision-extractions?session_id=${encodeURIComponent(sessionId)}`);
      setExtractions(res.data?.extractions || []);
      setSearch(res.data?.search || {});
    } catch (e: any) {
      setError(e?.message || 'Failed to load vision extractions');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ extractions, search }, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  };

  if (currentUser?.role !== 'admin' || !debugMode || !sessionId) return null;

  return (
    <>
      <button
        type="button"
        onClick={load}
        title="Admin only — everything the vision agent extracted from the photos in this chat"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#efebe2] bg-[#f6f4f0] text-[#999999] hover:bg-[#efebe2] hover:text-[#201e1a] text-xs font-medium flex-shrink-0 cursor-pointer"
      >
        <Eye className="w-3.5 h-3.5" />
        Vision
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#efebe2]">
              <Eye className="w-4 h-4 text-[#201e1a]" />
              <span className="font-semibold text-sm text-[#201e1a]">Vision extractions — {extractions.length} pass{extractions.length === 1 ? '' : 'es'}</span>
              {extractions.length > 0 && (
                <button
                  type="button"
                  onClick={copyJson}
                  className="ml-auto flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                >
                  {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy JSON'}
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} className={`${extractions.length > 0 ? '' : 'ml-auto '}text-[#999999] hover:text-[#201e1a]`}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 space-y-3 text-xs">
              {loading && <div className="flex items-center gap-2 text-[#6B6862] p-3"><Loader2 className="w-4 h-4 animate-spin" /> loading…</div>}
              {error && <div className="text-red-600 p-3">{error}</div>}
              {!loading && !error && extractions.length === 0 && (
                <div className="text-[#6B6862] p-3">No images analysed in this chat yet.</div>
              )}
              {!loading && !error && Object.keys(search).length > 0 && (
                <div data-testid="vision-search-use" className="rounded-lg border border-[#d9ecf3]">
                  <div className="px-3 py-2 bg-[#f3f9fb] border-b border-[#d9ecf3] text-[11px] font-semibold text-[#201e1a]">
                    What the search used
                  </div>
                  <div className="p-2 font-mono">
                    {Object.entries(search).map(([k, v]) => (
                      <JsonTreeNode key={k} nodeKey={k} value={v} depth={0} defaultExpanded />
                    ))}
                  </div>
                </div>
              )}
              {[...extractions].reverse().map((x, i) => {
                const raw = itemCount(x.raw);
                const cleaned = itemCount(x.cleaned);
                return (
                  <div key={i} className="rounded-lg border border-[#efebe2]">
                    <div className="flex items-center gap-3 px-3 py-2 bg-[#fafaf7] border-b border-[#efebe2] text-[11px]">
                      <span className="font-semibold text-[#201e1a]">#{extractions.length - i}</span>
                      {x.at && <span className="text-[#999999]">{new Date(x.at * 1000).toLocaleString()}</span>}
                      {x.parse_error
                        ? <span className="text-red-600">unparseable model output</span>
                        : <span className="text-[#6B6862]">
                            {cleaned} item{cleaned === 1 ? '' : 's'}
                            {x.raw && raw !== cleaned && <span className="text-amber-700"> · {raw - cleaned} dropped by cleanup (raw had {raw})</span>}
                          </span>}
                    </div>
                    <div className="p-2 font-mono">
                      {Object.entries(x)
                        .filter(([k]) => k !== 'at')
                        .map(([k, v]) => (
                          <JsonTreeNode key={k} nodeKey={k} value={v} depth={0} defaultExpanded={k === 'cleaned'} />
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VisionDebugPanel;
